import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as InboxActions from './inbox.actions';
import { FileItem, Files, IFile, IFileUploadResponseDTO, Message } from '@care-portals/api-interfaces';

export const INBOX_FEATURE_KEY = 'inbox';

export interface State extends EntityState<Message> {
  selectedId?: string | number; // which message record has been selected
  message: Message;
  fileItems: IFileUploadResponseDTO[]; // used as temp storage for uploading files
  files: Files;
  fileData: {};
  loading: boolean;
  uploading: boolean;
  error?: string | null; // last none error (if any)
}

export interface InboxPartialState {
  readonly [INBOX_FEATURE_KEY]: State;
}

export function selectMessageId(message: Message): string {
  return message._id;
}

export const messageAdapter: EntityAdapter<Message> =
  createEntityAdapter<Message>({
    selectId: selectMessageId
  });

export const initialState: State = messageAdapter.getInitialState({
  message: null,
  fileItems: [],
  files: {},
  fileData: {},
  loading: false,
  uploading: false
});

const inboxReducer = createReducer(
  initialState,
  on(InboxActions.loadMessages, state =>
    messageAdapter.removeAll({
      ...state,
      loading: true,
      error: null
    })
  ),
  on(InboxActions.loadMessagesSuccess, (state, { messages }) =>
    messageAdapter.setAll(messages, {
      ...state,
      loading: false
    })
  ),
  on(InboxActions.loadMessagesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(InboxActions.getFile, state => ({ ...state, loading: true, error: null })),
  on(InboxActions.getFileSuccess, (state, { file }) => ({
    ...state,
    fileData: { ...state.fileData, [file.name]: file },
    loading: false
  })),
  on(InboxActions.getFileFailure, (state, { error }) => ({ ...state, error })),

  on(InboxActions.getMessage, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(InboxActions.getMessageSuccess, (state, { message }) =>
    messageAdapter.upsertOne(message, {
      ...state,
      loading: false,
      selectedId: message._id
    })
  ),
  on(InboxActions.getMessageFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(InboxActions.setRawFile, (state, { resourceId, fileItem }) => {
    let files;
    if (!fileItem.buffer) {
      files = { ...state.files };
      delete files[resourceId];
    } else {
      files = {
        ...state.files,
        [resourceId]: { ...fileItem, loaded: true },
      }
    }
    return {
      ...state,
      files
    }
  }),

  on(InboxActions.uploadFiles, state => ({
    ...state,
    uploading: true,
    error: null
  })),
  on(InboxActions.uploadFilesSuccess, (state, { files }) => ({
    ...state,
    uploading: false,
    fileItems: files
  })),
  on(InboxActions.uploadFilesFailure, (state, { error }) => ({
    ...state,
    uploading: false,
    error
  })),

  on(InboxActions.replyMessage, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(InboxActions.replyMessageSuccess, (state, { message }) => {
    let fileData = state.fileData;
    if (message.file) {
      const fileName = message.file
        .split('/')
        .pop()
        .replace(/%3A/g, ':')
        .replace(/%20/g, ' ');
      fileData = { ...fileData, [fileName]: state.files['inbox'] };
    }

    return messageAdapter.addOne(message, {
      ...state,
      loading: false,
      fileData,
      files: {}
    });
  }),
  on(InboxActions.replyMessageFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return inboxReducer(state, action);
}
